/**
 * Serviço para gerenciar vinculações de professores a disciplinas e horários
 * Soluciona o problema de horários sem professor atribuído
 */

import { supabase } from "../supabaseClient"
import { applyInstitutionFilter } from "../lib/tenant"

/**
 * Atualiza automaticamente os professor_id dos horários
 * que têm disciplinas vinculadas a professores
 * 
 * @param {string} turmaId - ID da turma
 * @param {boolean} isAdmin - Se o usuário é admin
 * @param {string} instituicaoId - ID da instituição
 * @returns {Promise<{sucesso: number, atualizado: number, erro: number}>}
 */
export async function autoVincularProfessorES(turmaId, isAdmin, instituicaoId) {
  try {
    // 1. Buscar todos os horários da turma SEM professor
    let horariosQuery = supabase
      .from("horarios")
      .select(`
        id,
        hora_inicio,
        hora_fim,
        dia_semana,
        disciplina_id,
        professor_id,
        disciplina:disciplina_id(id, nome)
      `)
      .eq("turma_id", turmaId)
      .is("professor_id", null); // Apenas horários SEM professor

    horariosQuery = applyInstitutionFilter(horariosQuery, isAdmin, instituicaoId);

    const { data: horariosData, error: horariosError } = await horariosQuery;

    if (horariosError) throw horariosError;

    if (!horariosData || horariosData.length === 0) {
      return { sucesso: true, atualizado: 0, erro: 0, mensagem: "Nenhum horário sem professor para atualizar" };
    }

    // 2. Para cada horário, buscar o professor vinculado
    const atualizacoes = [];
    const erros = [];

    for (const horario of horariosData) {
      if (!horario.disciplina_id) continue;

      // Buscar professor vinculado à disciplina
      let pdQuery = supabase
        .from("professor_disciplina")
        .select("professor_id, professor:professor_id(id, nome)")
        .eq("disciplina_id", horario.disciplina_id);
      
      // Aplicar filtro de instituição se não for admin
      if (!isAdmin && instituicaoId) {
        pdQuery = pdQuery.eq("instituicao_id", instituicaoId);
      }
      
      const { data: pdData, error: pdError } = await pdQuery
        .limit(1)
        .single();

      if (pdError || !pdData) {
        erros.push({
          horario_id: horario.id,
          razao: "Nenhum professor vinculado a essa disciplina",
        });
        continue;
      }

      // Preparar atualização
      atualizacoes.push({
        horario_id: horario.id,
        professor_id: pdData.professor_id,
        professor_nome: pdData.professor?.nome,
      });
    }

    // 3. Executar atualizações em lote
    let atualizado = 0;
    for (const att of atualizacoes) {
      const { error } = await supabase
        .from("horarios")
        .update({ professor_id: att.professor_id })
        .eq("id", att.horario_id);

      if (error) {
        erros.push({
          horario_id: att.horario_id,
          razao: error.message,
        });
      } else {
        atualizado++;
      }
    }

    return {
      sucesso: true,
      atualizado,
      erro: erros.length,
      detalhesErros: erros,
      mensagem: `${atualizado} horários atualizados com professores vinculados${
        erros.length > 0 ? ` (${erros.length} erros)` : ""
      }`,
    };
  } catch (error) {
    console.error("Erro ao auto-vincular professores:", error);
    return {
      sucesso: false,
      atualizado: 0,
      erro: 1,
      mensagem: `Erro ao vincular professores: ${error.message}`,
    };
  }
}

/**
 * Busca professores vinculados a uma disciplina
 * @param {string} disciplinaId - ID da disciplina
 * @param {boolean} isAdmin - Se é admin
 * @param {string} instituicaoId - ID da instituição
 * @returns {Promise<Array>} Array de professores
 */
export async function buscarProfessoresDisciplina(disciplinaId, isAdmin, instituicaoId) {
  try {
    let query = supabase
      .from("professor_disciplina")
      .select("professor:professor_id(id, nome, email)")
      .eq("disciplina_id", disciplinaId);

    query = applyInstitutionFilter(query, isAdmin, instituicaoId);

    const { data, error } = await query;

    if (error) throw error;

    return (data || []).map((pd) => pd.professor).filter(Boolean);
  } catch (error) {
    console.error("Erro ao buscar professores:", error);
    return [];
  }
}

/**
 * Vincula um professor a uma disciplina
 * @param {string} professorId - ID do professor
 * @param {string} disciplinaId - ID da disciplina
 * @param {boolean} isAdmin - Se é admin
 * @param {string} instituicaoId - ID da instituição
 * @returns {Promise<{sucesso: boolean, mensagem: string}>}
 */
export async function vincularProfessorDisciplina(professorId, disciplinaId, isAdmin, instituicaoId) {
  try {
    // Verificar se já existe
    const { data: existente } = await supabase
      .from("professor_disciplina")
      .select("id")
      .eq("professor_id", professorId)
      .eq("disciplina_id", disciplinaId)
      .single();

    if (existente) {
      return { sucesso: false, mensagem: "Professor já vinculado a essa disciplina" };
    }

    const payload = {
      professor_id: professorId,
      disciplina_id: disciplinaId,
    };

    if (!isAdmin && instituicaoId) {
      payload.instituicao_id = instituicaoId;
    }

    const { error } = await supabase.from("professor_disciplina").insert([payload]);

    if (error) throw error;

    return { sucesso: true, mensagem: "Professor vinculado com sucesso" };
  } catch (error) {
    console.error("Erro ao vincular:", error);
    return { sucesso: false, mensagem: `Erro: ${error.message}` };
  }
}

/**
 * Remove vinculação entre professor e disciplina
 * @param {string} professorId - ID do professor
 * @param {string} disciplinaId - ID da disciplina
 * @returns {Promise<{sucesso: boolean, mensagem: string}>}
 */
export async function desvincularProfessorDisciplina(professorId, disciplinaId) {
  try {
    const { error } = await supabase
      .from("professor_disciplina")
      .delete()
      .eq("professor_id", professorId)
      .eq("disciplina_id", disciplinaId);

    if (error) throw error;

    return { sucesso: true, mensagem: "Desvinculação realizada com sucesso" };
  } catch (error) {
    console.error("Erro ao desvincular:", error);
    return { sucesso: false, mensagem: `Erro: ${error.message}` };
  }
}
